<script setup lang="ts">
import { useLocalStorage, useMediaQuery } from '@vueuse/core'
import { Temporal } from 'temporal-polyfill'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import type {
  GroupBoostLimit,
  PublicEventDetails,
  PublicSlot,
  UserRegistrationBoostOverview,
  UserRegistrationListItem,
} from '@/types'

import PersonalCalendarFeedCard from '@/components/user/PersonalCalendarFeedCard.vue'
import { toAppColor } from '@/composables/useAppColor'
import { appConfirm } from '@/composables/useAppConfirm'
import { useAppToast } from '@/composables/useAppToast'
import { useMetadata } from '@/composables/useMetadata'
import { registrationStatusColor } from '@/composables/useRegistrationStatusColor'
import { eventService } from '@/services/eventService'
import userService from '@/services/userService'
import {
  EventType,
  RegistrationBoostMode,
  RegistrationBoostState,
  RegistrationStatus,
  Visibility,
} from '@/types'
import { SlotType } from '@/types/slot'
import { getApiErrorMessage } from '@/utils/apiError'
import { getCampusEventTitle, getPublicCampusEventUrl } from '@/utils/campusEvent'

definePage({
  meta: {
    requiresAuth: true,
  },
})

const { t, d, n, locale } = useI18n()

// On phones the card actions carry text labels: an unlabeled "x" or envelope is easy to misread.
const isPhone = useMediaQuery('(max-width: 639px)')
// The sidebar leaves too little width for the fee and boost columns on notebooks, so those use cards.
const isXlScreen = useMediaQuery('(min-width: 1280px)')
const isWideScreen = useMediaQuery('(min-width: 1680px)')
const route = useRoute()
const router = useRouter()
const toast = useAppToast()
const { availableSemesters, initializeMetadata } = useMetadata()

const registrations = ref<UserRegistrationListItem[]>([])
const selectedSemesterFilter = useLocalStorage('signmeup:registrations:filter:v2', 'current')
const registrationsLoaded = ref(false)
const boostOverview = ref<UserRegistrationBoostOverview | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
const offerResponseLoadingId = ref<number | null>(null)
const attachmentDeleteLoadingId = ref<string | null>(null)
const boostToggleLoadingId = ref<number | null>(null)
const activatedBoostRegistrationId = ref<number | null>(null)
const contactRegistration = ref<UserRegistrationListItem | null>(null)
const previewRegistration = ref<UserRegistrationListItem | null>(null)
const previewVisible = ref(false)
const queryActionHandled = ref(false)
const eventDetailsCache = ref<Record<number, PublicEventDetails>>({})
const assignedSlotDetailsByRegistration = ref<
  Record<number, { slot: PublicSlot | null; disableWaitlist: boolean; loadError: boolean }>
>({})
let boostAnimationTimeout: ReturnType<typeof setTimeout> | null = null
const recentRegistrationCutoff = Temporal.Now.zonedDateTimeISO().subtract({ months: 3 }).toInstant()

const eventName = (registration: UserRegistrationListItem) => {
  return locale.value === 'en'
    ? registration.eventNameEN || registration.eventNameDE
    : registration.eventNameDE
}

const eventCaption = (registration: UserRegistrationListItem) => {
  return locale.value === 'en'
    ? registration.eventCaptionEN || registration.eventCaptionDE
    : registration.eventCaptionDE || registration.eventCaptionEN
}

const formatAttachmentSize = (size: number) => `${(size / 1024 / 1024).toFixed(2)} MB`

async function downloadRegistrationAttachment(
  registration: UserRegistrationListItem,
  attachmentId: string,
  fileName: string,
) {
  try {
    await userService.downloadRegistrationAttachment(
      registration.registrationId,
      attachmentId,
      fileName,
    )
  } catch {
    toast.add({
      color: 'error',
      title: t('pages.registrations.files.downloadError'),
      duration: 4000,
    })
  }
}

async function confirmDeleteRegistrationAttachment(
  registration: UserRegistrationListItem,
  attachmentId: string,
  fileName: string,
) {
  const accepted = await appConfirm({
    title: t('pages.registrations.files.deleteTitle'),
    message: t('pages.registrations.files.deleteConfirm', { name: fileName }),
    cancelLabel: t('common.actions.cancel'),
    confirmLabel: t('common.actions.delete'),
    confirmColor: 'error',
    confirmIcon: 'i-lucide-trash-2',
  })
  if (!accepted) return

  attachmentDeleteLoadingId.value = attachmentId
  try {
    await userService.deleteRegistrationAttachment(registration.registrationId, attachmentId)
    registration.attachments = registration.attachments?.filter(
      (attachment) => attachment.id !== attachmentId,
    )
    toast.add({
      color: 'success',
      title: t('pages.registrations.files.deleteSuccess'),
      duration: 3000,
    })
  } catch {
    toast.add({
      color: 'error',
      title: t('pages.registrations.files.deleteError'),
      duration: 4000,
    })
  } finally {
    attachmentDeleteLoadingId.value = null
  }
}

const groupNames = (registration: UserRegistrationListItem) => {
  return registration.groups
    .map((group) =>
      locale.value === 'en'
        ? group.nameEN || group.nameDE || group.customId
        : group.nameDE || group.nameEN || group.customId,
    )
    .join(', ')
}

const registrationPeriodText = (registration: UserRegistrationListItem) => {
  if (registration.registrationStart && registration.registrationEnd) {
    return `${d(new Date(registration.registrationStart), 'datetime')} - ${d(new Date(registration.registrationEnd), 'datetime')}`
  }
  if (registration.registrationStart) {
    return `${t('pages.registrations.registrationPeriod.starts')} ${d(new Date(registration.registrationStart), 'datetime')}`
  }
  if (registration.registrationEnd) {
    return `${t('pages.registrations.registrationPeriod.ends')} ${d(new Date(registration.registrationEnd), 'datetime')}`
  }
  return t('pages.registrations.registrationPeriod.unlimited')
}

const registrationPeriodInfo = (registration: UserRegistrationListItem) => {
  const now = Date.now()
  const start = registration.registrationStart
    ? new Date(registration.registrationStart).getTime()
    : null
  const end = registration.registrationEnd ? new Date(registration.registrationEnd).getTime() : null

  if (end != null && now > end) {
    return { key: 'ended', color: toAppColor('secondary') } as const
  }
  if (start != null && now < start) {
    return { key: 'upcoming', color: toAppColor('info') } as const
  }
  return { key: 'open', color: toAppColor('success') } as const
}

const registrationPeriodState = (registration: UserRegistrationListItem) =>
  t(`pages.offer.state.${registrationPeriodInfo(registration).key}`)

// Compact schedule line: a pending allocation outranks the registration period because it is the
// next thing that happens; otherwise the next (or, once ended, the last) registration date.
const registrationSchedule = (registration: UserRegistrationListItem) => {
  const format = (value: string) => d(new Date(value), 'datetime')
  if (registration.firstDrawDate && new Date(registration.firstDrawDate).getTime() > Date.now()) {
    return {
      label: t('pages.registrations.registrationPeriod.allocation'),
      date: format(registration.firstDrawDate),
    }
  }
  if (registrationPeriodInfo(registration).key === 'upcoming' && registration.registrationStart) {
    return {
      label: t('pages.registrations.registrationPeriod.starts'),
      date: format(registration.registrationStart),
    }
  }
  if (registration.registrationEnd) {
    return {
      label: t('pages.registrations.registrationPeriod.ends'),
      date: format(registration.registrationEnd),
    }
  }
  return { label: t('pages.registrations.registrationPeriod.unlimited'), date: '' }
}

const usageText = (registration: UserRegistrationListItem) => {
  const places =
    registration.placesNumber && registration.placesNumber > 0 ? registration.placesNumber : '∞'
  return `${registration.registrationCount} / ${places}`
}

const dayNames = () => [
  t('common.days.sunday'),
  t('common.days.monday'),
  t('common.days.tuesday'),
  t('common.days.wednesday'),
  t('common.days.thursday'),
  t('common.days.friday'),
  t('common.days.saturday'),
]

const slotTypeLabel = (type: SlotType) => {
  switch (type) {
    case SlotType.Topic:
      return t('components.registration.slotType.topic')
    case SlotType.Appointment:
      return t('components.registration.slotType.appointment')
    case SlotType.WeeklyAppointment:
      return t('components.registration.slotType.weeklyAppointment')
    case SlotType.CampusEvent:
      return t('components.registration.slotType.campusEvent')
    case SlotType.Person:
      return t('components.registration.slotType.person')
    default:
      return ''
  }
}

const localizedCampusSlotName = (slot: PublicSlot) =>
  getCampusEventTitle(slot.campusEvent, locale.value) || undefined

const slotDisplayName = (slot: PublicSlot) =>
  slot.name ||
  (slot.type === SlotType.CampusEvent ? localizedCampusSlotName(slot) : undefined) ||
  slotTypeLabel(slot.type)

const publicCampusEventUrl = (slot: PublicSlot) => {
  if (!slot.campusEventGuid) {
    return null
  }

  return getPublicCampusEventUrl(slot.campusEventGuid)
}

const slotTimeInfo = (slot: PublicSlot) => {
  if (slot.type === SlotType.Appointment && slot.startTime) {
    const start = d(new Date(slot.startTime), 'datetime')
    const end = slot.endTime ? d(new Date(slot.endTime), 'timeonly') : ''
    return end ? `${start} - ${end}` : start
  }

  if (slot.type === SlotType.WeeklyAppointment && slot.dayOfWeek !== undefined) {
    const dayIndex =
      typeof slot.dayOfWeek === 'number'
        ? slot.dayOfWeek
        : ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].indexOf(
            slot.dayOfWeek,
          )

    const dayName = dayNames()[dayIndex] || ''
    const startTime = slot.startTimeOnly?.slice(0, 5) || ''
    const endTime = slot.endTimeOnly?.slice(0, 5) || ''

    if (startTime && endTime) {
      return `${dayName}, ${startTime} - ${endTime}`
    }

    return dayName
  }

  if (slot.type === SlotType.CampusEvent) {
    const parts = [
      slot.campusEvent?.firstAppointment
        ? d(new Date(slot.campusEvent.firstAppointment), 'datetime')
        : null,
      slot.campusEvent?.type ?? null,
    ].filter(Boolean)

    return parts.join(' - ')
  }

  return ''
}

const feeStateSeverity = (feeState?: string) => {
  switch (feeState) {
    case 'paid':
      return 'success'
    case 'unpaid':
      return 'warn'
    default:
      return 'secondary'
  }
}

const hasWaitlistPosition = (registration: UserRegistrationListItem) =>
  registration.status === RegistrationStatus.Waitlist && registration.waitlistPosition != null

const hasWaitlistPositionRange = (registration: UserRegistrationListItem) =>
  registration.status === RegistrationStatus.Waitlist && registration.waitlistPositionRange != null

const waitlistPositionLabel = (registration: UserRegistrationListItem, short = false) => {
  if (hasWaitlistPosition(registration)) {
    return t(
      short
        ? 'pages.registrations.waitlistPositionShort'
        : 'pages.registrations.waitlistPositionLabel',
      { position: registration.waitlistPosition },
    )
  }

  if (hasWaitlistPositionRange(registration)) {
    return t(
      short
        ? 'pages.registrations.waitlistPositionEstimateShort'
        : 'pages.registrations.waitlistPositionEstimateLabel',
      {
        range: t(`pages.registrations.waitlistPositionRange.${registration.waitlistPositionRange}`),
      },
    )
  }

  return null
}

const replaceFeePlaceholders = (
  message: string | undefined,
  registration: UserRegistrationListItem,
) => {
  if (!message) {
    return ''
  }

  const feeText = registration.feeAmount != null ? n(registration.feeAmount, 'currency') : ''
  const registrationId = registration.registrationId.toString()
  const feeReferenceId = registration.feeReferenceId ?? ''
  const sepaCreditor = registration.sepaCreditorName ?? ''
  const sepaIban = registration.sepaIban ?? ''
  const sepaBic = registration.sepaBic ?? ''

  return message
    .replace(/#FEE#/gi, feeText)
    .replace(/##REGID##/g, registrationId)
    .replace(/#REFERENCEID#/gi, feeReferenceId)
    .replace(/#SEPACREDITOR#/gi, sepaCreditor)
    .replace(/#SEPAIBAN#/gi, sepaIban)
    .replace(/#SEPABIC#/gi, sepaBic)
}

const feeNotificationText = (registration: UserRegistrationListItem) => {
  const message =
    locale.value === 'en'
      ? registration.feeNotificationTextEN || registration.feeNotificationTextDE
      : registration.feeNotificationTextDE || registration.feeNotificationTextEN
  return replaceFeePlaceholders(message, registration)
}

const showFeeNotice = (registration: UserRegistrationListItem) =>
  registration.feeState === 'unpaid' && !!feeNotificationText(registration)

const contactSubject = (registration: UserRegistrationListItem) =>
  `SignMeUp: ${eventName(registration)}`

const contactMailto = (registration: UserRegistrationListItem) => {
  if (!registration.contactEmail) {
    return ''
  }
  return `mailto:${registration.contactEmail}?subject=${encodeURIComponent(contactSubject(registration))}`
}

const handleContactDialogVisibility = (visible: boolean) => {
  if (!visible) {
    contactRegistration.value = null
  }
}

const openPreview = (registration: UserRegistrationListItem) => {
  previewRegistration.value = registration
  previewVisible.value = true
}

const handlePreviewVisibility = (visible: boolean) => {
  previewVisible.value = visible
  if (!visible) {
    previewRegistration.value = null
  }
}

const isAppointmentRegistration = (registration: UserRegistrationListItem) =>
  !!registration.appointmentStart

// A questionnaire response is accepted on submission and can be edited or withdrawn until the response
// period ends, after which it is frozen.
const isQuestionnaireRegistration = (registration: UserRegistrationListItem) =>
  registration.eventType === EventType.Questionnaire

const isResponsePeriodOpen = (registration: UserRegistrationListItem) =>
  !registration.registrationEnd || Date.now() <= new Date(registration.registrationEnd).getTime()

const editLabel = (registration: UserRegistrationListItem) =>
  isQuestionnaireRegistration(registration)
    ? t('pages.registrations.questionnaire.editAnswers')
    : t('common.actions.edit')

const unregisterLabel = (registration: UserRegistrationListItem) =>
  isQuestionnaireRegistration(registration)
    ? t('pages.registrations.questionnaire.withdraw')
    : t('pages.registrations.actions.unregister')

// An open appointment is a request waiting for the event team, which "Offen" alone does not say.
const registrationStatusLabel = (registration: UserRegistrationListItem) =>
  isQuestionnaireRegistration(registration)
    ? t('pages.registrations.questionnaire.submitted')
    : isAppointmentRegistration(registration) && registration.status === RegistrationStatus.Open
      ? t('pages.registrations.appointmentPending')
      : t(`enums.registrationStatus.${registration.status}`)

const isEditDisabled = (registration: UserRegistrationListItem) =>
  isQuestionnaireRegistration(registration)
    ? !isResponsePeriodOpen(registration)
    : isAppointmentRegistration(registration) ||
      (registration.status === RegistrationStatus.Accepted &&
        registration.canSwitchAcceptedSlot === false)

const boostStateByRegistration = computed(
  () =>
    new Map(
      (boostOverview.value?.registrations ?? []).map((state) => [state.registrationId, state]),
    ),
)

const boostLimitRows = computed(() => boostOverview.value?.groupLimits ?? [])

const defaultSemesterId = computed(
  () =>
    availableSemesters.value.find((semester) => semester.isDefault)?.id ??
    availableSemesters.value.find((semester) => semester.isCurrentlyActive)?.id ??
    null,
)

const isRecentRegistration = (registration: UserRegistrationListItem) => {
  try {
    return (
      Temporal.Instant.compare(
        Temporal.Instant.from(registration.createdAt),
        recentRegistrationCutoff,
      ) >= 0
    )
  } catch {
    return false
  }
}

const isCurrentRegistration = (registration: UserRegistrationListItem) =>
  registration.semesterId === defaultSemesterId.value || isRecentRegistration(registration)

const semesterOptions = computed(() => {
  const semesters = new Map<number, { nameDE?: string | null; nameEN?: string | null }>()
  for (const registration of registrations.value) {
    if (registration.semesterId == null) {
      continue
    }
    semesters.set(registration.semesterId, {
      nameDE: registration.semesterNameDE,
      nameEN: registration.semesterNameEN,
    })
  }

  const options = [...semesters.entries()]
    .sort(([a], [b]) => b - a)
    .map(([id, semester]) => ({
      label:
        locale.value === 'en'
          ? semester.nameEN || semester.nameDE || id.toString()
          : semester.nameDE || semester.nameEN || id.toString(),
      value: id.toString(),
    }))

  return [
    { label: t('pages.registrations.filters.currentRegistrations'), value: 'current' },
    { label: t('pages.registrations.filters.allSemesters'), value: 'all' },
    ...options,
  ]
})

const filteredRegistrations = computed(() => {
  if (selectedSemesterFilter.value === 'all') {
    return registrations.value
  }
  if (selectedSemesterFilter.value === 'current') {
    return registrations.value.filter(isCurrentRegistration)
  }
  return registrations.value.filter(
    (registration) => registration.semesterId?.toString() === selectedSemesterFilter.value,
  )
})

const filteredBoostLimitRows = computed(() => {
  if (selectedSemesterFilter.value === 'all') {
    return boostLimitRows.value
  }
  if (selectedSemesterFilter.value === 'current') {
    return boostLimitRows.value.filter((limit) => limit.semesterId === defaultSemesterId.value)
  }
  return boostLimitRows.value.filter(
    (limit) => limit.semesterId.toString() === selectedSemesterFilter.value,
  )
})

const showFeeColumn = computed(() =>
  filteredRegistrations.value.some((registration) => registration.feeAmount != null),
)

const localizedGroupBoostName = (limit: GroupBoostLimit) =>
  locale.value === 'en'
    ? limit.groupNameEN || limit.groupNameDE
    : limit.groupNameDE || limit.groupNameEN || limit.groupCustomId

const localizedSemesterName = (limit: GroupBoostLimit) =>
  locale.value === 'en'
    ? limit.semesterNameEN || limit.semesterNameDE
    : limit.semesterNameDE || limit.semesterNameEN

const boostLimitUsedText = (limit: GroupBoostLimit) =>
  limit.effectiveLimit === -1
    ? t('pages.registrations.boost.usedUnlimited', {
        current: limit.currentBoosts,
      })
    : t('pages.registrations.boost.usedLimited', {
        current: limit.currentBoosts,
        limit: limit.effectiveLimit,
      })

const boostLimitRemainingText = (limit: GroupBoostLimit) =>
  limit.effectiveLimit === -1
    ? t('pages.registrations.boost.noLimit')
    : t('pages.registrations.boost.remainingCount', {
        count: Math.max(0, limit.effectiveLimit - limit.currentBoosts),
      })

const getBoostState = (registration: UserRegistrationListItem) =>
  boostStateByRegistration.value.get(registration.registrationId)

const shouldShowBoostControls = (registration: UserRegistrationListItem) =>
  getBoostState(registration)?.boostsEnabled ?? false

// Optimization events boost a slot rating instead of the registration; the registration toggle then
// only appears to remove a leftover boost.
const isSlotBoostMode = (registration: UserRegistrationListItem) =>
  getBoostState(registration)?.boostMode === RegistrationBoostMode.Slot

const shouldShowBoostToggle = (registration: UserRegistrationListItem) =>
  shouldShowBoostControls(registration) &&
  (!isSlotBoostMode(registration) || isBoosted(registration))

const slotBoostCount = (registration: UserRegistrationListItem) =>
  getBoostState(registration)?.boostedSlotIds.length ?? 0

const canBoostSlot = (registration: UserRegistrationListItem) =>
  isSlotBoostMode(registration) &&
  shouldShowBoostControls(registration) &&
  registration.status === RegistrationStatus.Open

const showBoostColumn = computed(() => filteredRegistrations.value.some(shouldShowBoostControls))

const useTableLayout = computed(() =>
  showFeeColumn.value || showBoostColumn.value ? isWideScreen.value : isXlScreen.value,
)

const isBoosted = (registration: UserRegistrationListItem) =>
  getBoostState(registration)?.isBoosted ?? false

const canToggleBoost = (registration: UserRegistrationListItem) => {
  const state = getBoostState(registration)?.boostState
  return (
    state === RegistrationBoostState.BoostAllowed || state === RegistrationBoostState.UnboostAllowed
  )
}

const boostButtonLabel = (registration: UserRegistrationListItem) => {
  if (isBoosted(registration)) {
    return t('pages.registrations.boost.active')
  }
  return t('pages.registrations.boost.inactive')
}

const boostStateMessage = (registration: UserRegistrationListItem) => {
  const state = getBoostState(registration)?.boostState ?? RegistrationBoostState.NoBoostsAllowed
  return t(`pages.registrations.boost.states.${state}`)
}

const triggerBoostActivationAnimation = (registrationId: number) => {
  activatedBoostRegistrationId.value = registrationId
  if (boostAnimationTimeout) {
    clearTimeout(boostAnimationTimeout)
  }
  boostAnimationTimeout = setTimeout(() => {
    if (activatedBoostRegistrationId.value === registrationId) {
      activatedBoostRegistrationId.value = null
    }
    boostAnimationTimeout = null
  }, 900)
}

const toggleBoost = async (registration: UserRegistrationListItem) => {
  boostToggleLoadingId.value = registration.registrationId
  const wasBoosted = isBoosted(registration)

  try {
    boostOverview.value = await userService.toggleRegistrationBoost(registration.registrationId)
    const nowBoosted = isBoosted(registration)
    if (!wasBoosted && nowBoosted) {
      triggerBoostActivationAnimation(registration.registrationId)
    }

    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: nowBoosted
        ? t('pages.registrations.boost.activateSuccess')
        : t('pages.registrations.boost.deactivateSuccess'),
      duration: 3000,
    })
  } catch (err) {
    console.error('Failed to toggle boost:', err)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('pages.registrations.boost.toggleError'),
      duration: 4000,
    })
  } finally {
    if (boostToggleLoadingId.value === registration.registrationId) {
      boostToggleLoadingId.value = null
    }
  }
}

const eventRouteId = (registration: UserRegistrationListItem) => {
  return registration.visibility === Visibility.Public
    ? registration.eventId.toString()
    : registration.privateLinkId
}

function findSlotById(slots: PublicSlot[], slotId: number): PublicSlot | null {
  for (const slot of slots) {
    if (slot.id === slotId) {
      return slot
    }

    if (slot.subSlots.length > 0) {
      const nestedMatch = findSlotById(slot.subSlots, slotId)
      if (nestedMatch) {
        return nestedMatch
      }
    }
  }

  return null
}

async function loadEventDetails(
  registration: UserRegistrationListItem,
): Promise<PublicEventDetails> {
  const cached = eventDetailsCache.value[registration.eventId]
  if (cached) {
    return cached
  }

  const details = await eventService.getPublicEventDetails(eventRouteId(registration))
  eventDetailsCache.value = {
    ...eventDetailsCache.value,
    [registration.eventId]: details,
  }

  return details
}

const getAssignedSlotDetails = (registration: UserRegistrationListItem) => {
  return assignedSlotDetailsByRegistration.value[registration.registrationId]
}

const preloadAssignedSlotDetails = async (items: UserRegistrationListItem[]) => {
  const registrationsWithAssignedSlots = items.filter(
    (registration) => registration.assignedSlotId != null,
  )
  if (registrationsWithAssignedSlots.length === 0) {
    assignedSlotDetailsByRegistration.value = {}
    return
  }

  const uniqueEventRegistrations = Array.from(
    new Map(
      registrationsWithAssignedSlots.map((registration) => [registration.eventId, registration]),
    ).values(),
  )

  await Promise.all(
    uniqueEventRegistrations.map(async (registration) => {
      try {
        await loadEventDetails(registration)
      } catch (err) {
        console.error('Failed to preload event details:', err)
      }
    }),
  )

  const details: Record<
    number,
    { slot: PublicSlot | null; disableWaitlist: boolean; loadError: boolean }
  > = {}

  registrationsWithAssignedSlots.forEach((registration) => {
    const eventDetails = eventDetailsCache.value[registration.eventId]
    if (!eventDetails || registration.assignedSlotId == null) {
      details[registration.registrationId] = {
        slot: null,
        disableWaitlist: false,
        loadError: true,
      }
      return
    }

    details[registration.registrationId] = {
      slot: findSlotById(eventDetails.slots, registration.assignedSlotId),
      disableWaitlist: eventDetails.disableWaitlist,
      loadError: false,
    }
  })

  assignedSlotDetailsByRegistration.value = details
}

const assignedSlotErrorText = (registration: UserRegistrationListItem) => {
  const details = getAssignedSlotDetails(registration)
  if (!details?.loadError) {
    return null
  }

  return t('pages.registrations.assignedSlot.detailsLoadError')
}

const goToEdit = (registration: UserRegistrationListItem) => {
  router.push(`/registrations/${registration.registrationId}`)
}

const goToEvent = (registration: UserRegistrationListItem) => {
  router.push(`/event/${eventRouteId(registration)}`)
}

// Copies the invite link rather than the bare code: the link opens the event with the code
// already filled in, which is what people paste into a chat.
const copyStudyGroupInvite = async (registration: UserRegistrationListItem) => {
  if (!registration.joinCode) {
    return
  }

  const url = `${window.location.origin}/event/${eventRouteId(registration)}?joinCode=${registration.joinCode}`

  try {
    await navigator.clipboard.writeText(url)
    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: t('pages.registrations.studyGroup.shareInviteCopied'),
      duration: 3000,
    })
  } catch {
    // Clipboard access can be denied by the browser. Show the link so it can still be copied
    // by hand rather than failing silently.
    toast.add({
      color: 'warning',
      title: t('pages.registrations.studyGroup.shareInviteCopyFailed'),
      description: url,
      duration: 10000,
    })
  }
}

const confirmUnregister = async (registration: UserRegistrationListItem) => {
  if (!isDeregistrationPossible(registration)) {
    toast.add({
      color: 'warning',
      title: t('common.messages.warning'),
      description: isQuestionnaireRegistration(registration)
        ? t('pages.registrations.questionnaire.withdrawClosed')
        : t('pages.registrations.actions.deregistrationDeadlineReached'),
      duration: 4000,
    })
    return
  }

  const accepted = await appConfirm({
    title: isQuestionnaireRegistration(registration)
      ? t('pages.registrations.questionnaire.withdrawConfirmHeader')
      : t('pages.registrations.actions.unregisterConfirmHeader'),
    message: isQuestionnaireRegistration(registration)
      ? t('pages.registrations.questionnaire.withdrawConfirmMessage', {
          name: eventName(registration),
        })
      : t('pages.registrations.actions.unregisterConfirmMessage', {
          name: eventName(registration),
        }),
    cancelLabel: t('common.actions.cancel'),
    confirmLabel: unregisterLabel(registration),
    confirmColor: 'error',
  })
  if (accepted) await unregister(registration)
}

const unregisterFromPreview = async (registration: UserRegistrationListItem) => {
  await confirmUnregister(registration)
  if (!registrations.value.some((item) => item.registrationId === registration.registrationId)) {
    handlePreviewVisibility(false)
  }
}

const isOfferResponseLoading = (registrationId: number) =>
  offerResponseLoadingId.value === registrationId

const isDeregistrationPossible = (registration: UserRegistrationListItem) =>
  isQuestionnaireRegistration(registration)
    ? isResponsePeriodOpen(registration)
    : !registration.deregistrationDeadline ||
      Date.now() < new Date(registration.deregistrationDeadline).getTime()

function getOfferResponseErrorMessage(err: unknown): string {
  return getApiErrorMessage(err, t, t('pages.registrations.actions.offerResponseError'))
}

const confirmAcceptOffer = async (registration: UserRegistrationListItem) => {
  const accepted = await appConfirm({
    title: t('pages.registrations.actions.acceptOfferConfirmHeader'),
    message: t('pages.registrations.actions.acceptOfferConfirmMessage', {
      name: eventName(registration),
    }),
    cancelLabel: t('common.actions.cancel'),
    confirmLabel: t('pages.registrations.actions.acceptOffer'),
    confirmIcon: 'i-lucide-circle-check',
  })
  if (accepted) await respondToOffer(registration, 'accept')
}

const confirmDeclineOffer = async (registration: UserRegistrationListItem) => {
  const accepted = await appConfirm({
    title: t('pages.registrations.actions.declineOfferConfirmHeader'),
    message: t('pages.registrations.actions.declineOfferConfirmMessage', {
      name: eventName(registration),
    }),
    cancelLabel: t('common.actions.cancel'),
    confirmLabel: t('pages.registrations.actions.declineOffer'),
    confirmColor: 'error',
  })
  if (accepted) await respondToOffer(registration, 'decline')
}

const respondToOffer = async (
  registration: UserRegistrationListItem,
  action: 'accept' | 'decline',
) => {
  offerResponseLoadingId.value = registration.registrationId

  try {
    if (action === 'accept') {
      await userService.acceptRegistrationOffer(registration.registrationId)
    } else {
      await userService.declineRegistrationOffer(registration.registrationId)
    }

    await loadRegistrations()

    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description:
        action === 'accept'
          ? t('pages.registrations.actions.acceptOfferSuccess')
          : t('pages.registrations.actions.declineOfferSuccess'),
      duration: 4000,
    })
  } catch (err) {
    console.error('Failed to respond to offer:', err)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: getOfferResponseErrorMessage(err),
      duration: 4000,
    })
  } finally {
    if (offerResponseLoadingId.value === registration.registrationId) {
      offerResponseLoadingId.value = null
    }
  }
}

const unregister = async (registration: UserRegistrationListItem) => {
  try {
    await userService.unregisterRegistration(registration.registrationId)
    await loadRegistrations()
    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: t('pages.registrations.actions.unregisterSuccess'),
      duration: 4000,
    })
  } catch (err) {
    console.error('Failed to unregister:', err)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('pages.registrations.actions.unregisterError'),
      duration: 4000,
    })
  }
}

const loadRegistrations = async () => {
  loading.value = true
  error.value = null

  try {
    const [items, boosts] = await Promise.all([
      userService.getRegistrations(),
      userService.getRegistrationBoosts(),
    ])
    registrations.value = items
    registrationsLoaded.value = true
    boostOverview.value = boosts
    await preloadAssignedSlotDetails(items)
    handleQueryAction(items)
  } catch (err: any) {
    console.error('Failed to load registrations:', err)
    error.value = getApiErrorMessage(err, t, t('pages.registrations.loadError'))
  } finally {
    loading.value = false
  }
}

function handleQueryAction(items: UserRegistrationListItem[]) {
  if (queryActionHandled.value) {
    return
  }

  const action = route.query.action
  const id = Number(route.query.id)
  if (typeof action !== 'string' || !Number.isFinite(id)) {
    return
  }

  const registration = items.find((item) => item.registrationId === id)
  if (!registration) {
    return
  }

  queryActionHandled.value = true
  if (action === 'confirm' && registration.status === RegistrationStatus.Offer) {
    confirmAcceptOffer(registration)
  } else if (action === 'unregister') {
    confirmUnregister(registration)
  }
}

onMounted(async () => {
  await Promise.all([initializeMetadata(), loadRegistrations()])
})

watch([semesterOptions, registrationsLoaded], ([options, loaded]) => {
  if (!loaded) {
    return
  }
  if (!options.some((option) => option.value === selectedSemesterFilter.value)) {
    selectedSemesterFilter.value = 'current'
  }
})

onUnmounted(() => {
  if (boostAnimationTimeout) {
    clearTimeout(boostAnimationTimeout)
  }
})
</script>

<template>
  <div class="p-4 sm:p-6">
    <div class="mb-6">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
          {{ t('pages.registrations.title') }}
        </h1>
        <p class="text-muted">
          {{ t('pages.registrations.subtitle') }}
        </p>
      </div>
      <div class="mt-4">
        <AppSelect
          v-model="selectedSemesterFilter"
          :options="semesterOptions"
          option-label="label"
          option-value="value"
          :aria-label="t('pages.registrations.filters.semester')"
          class="w-full sm:w-56"
        />
        <p v-if="selectedSemesterFilter === 'current'" class="mt-1 text-xs text-muted">
          {{ t('pages.registrations.filters.currentDescription') }}
        </p>
      </div>
    </div>

    <AppAlert v-if="error" color="error" :closable="false" class="mb-6">
      {{ error }}
    </AppAlert>

    <div
      v-if="filteredBoostLimitRows.length > 0"
      class="mb-6 grid gap-3 md:grid-cols-2 lg:grid-cols-3"
    >
      <div
        v-for="limit in filteredBoostLimitRows"
        :key="`${limit.semesterId}-${limit.groupId}`"
        class="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm dark:border-neutral-700 dark:bg-neutral-900"
      >
        <div class="flex items-start justify-between gap-3">
          <div>
            <div class="text-xs font-medium text-muted uppercase">
              {{ t('pages.registrations.boost.limitTitle') }}
            </div>
            <div class="mt-1 font-semibold text-neutral-900 dark:text-white">
              {{ localizedGroupBoostName(limit) }}
            </div>
          </div>
          <span class="text-xl" aria-hidden="true">🚀</span>
        </div>

        <dl class="mt-3 grid gap-2 text-sm">
          <div class="flex items-center justify-between gap-3">
            <dt class="text-muted">{{ t('pages.registrations.boost.semesterLabel') }}</dt>
            <dd class="font-medium text-neutral-800 dark:text-neutral-100">
              {{ localizedSemesterName(limit) }}
            </dd>
          </div>
          <div class="flex items-center justify-between gap-3">
            <dt class="text-muted">{{ t('pages.registrations.boost.usedLabel') }}</dt>
            <dd class="font-medium text-neutral-800 dark:text-neutral-100">
              {{ boostLimitUsedText(limit) }}
            </dd>
          </div>
          <div class="flex items-center justify-between gap-3">
            <dt class="text-muted">{{ t('pages.registrations.boost.remainingLabel') }}</dt>
            <dd class="font-medium text-neutral-800 dark:text-neutral-100">
              {{ boostLimitRemainingText(limit) }}
            </dd>
          </div>
        </dl>

        <div
          class="mt-3 rounded-md bg-neutral-50 px-3 py-2 text-xs text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300"
        >
          {{ t('pages.registrations.boost.limitScope') }}
        </div>
      </div>
    </div>

    <!-- Flat on phones: the registration cards are already cards, nesting them wastes width. -->
    <UCard
      :ui="{
        root: 'max-sm:bg-transparent max-sm:shadow-none max-sm:ring-0',
        body: 'max-sm:p-0',
      }"
    >
      <template #default>
        <div v-if="loading" class="flex justify-center py-10">
          <AppSpinner />
        </div>

        <div v-else-if="registrations.length === 0" class="py-8 text-center text-muted">
          <UIcon name="i-lucide-inbox" class="mb-3 text-4xl" />
          <p>{{ t('pages.registrations.empty') }}</p>
        </div>

        <div v-else-if="filteredRegistrations.length === 0" class="py-8 text-center text-muted">
          <UIcon name="i-lucide-funnel-x" class="mb-3 text-4xl" />
          <p>
            {{
              t(
                selectedSemesterFilter === 'current'
                  ? 'pages.registrations.filters.emptyCurrent'
                  : 'pages.registrations.filters.emptySemester',
              )
            }}
          </p>
        </div>

        <div v-else>
          <div v-if="!useTableLayout" class="space-y-3">
            <article
              v-for="registration in filteredRegistrations"
              :key="registration.registrationId"
              class="cursor-pointer rounded-lg border border-neutral-200 bg-white p-3 shadow-sm transition-colors hover:bg-neutral-50 sm:p-4 dark:border-neutral-700 dark:bg-neutral-900 dark:hover:bg-neutral-800"
              @click="openPreview(registration)"
            >
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0 flex-1 space-y-2">
                  <div class="flex flex-wrap items-center gap-2">
                    <UBadge color="neutral">
                      {{ t(`enums.eventType.${registration.eventType}`) }}
                    </UBadge>
                    <UBadge :color="registrationStatusColor(registration.status)">
                      {{ registrationStatusLabel(registration) }}
                    </UBadge>
                    <UBadge
                      v-if="(registration.groupMemberCount ?? 1) > 1"
                      color="neutral"
                      variant="subtle"
                      icon="i-lucide-users"
                    >
                      {{
                        t('pages.registrations.studyGroup.badge', {
                          count: registration.groupMemberCount,
                        })
                      }}
                    </UBadge>
                    <UButton
                      v-if="registration.joinCode"
                      icon="i-lucide-link"
                      color="neutral"
                      variant="subtle"
                      size="xs"
                      :label="t('pages.registrations.studyGroup.shareInvite')"
                      @click.stop="copyStudyGroupInvite(registration)"
                    />
                  </div>

                  <button
                    type="button"
                    class="block max-w-full text-left text-base font-semibold text-[var(--app-text-primary)] hover:underline"
                    @click.stop="goToEvent(registration)"
                  >
                    {{ eventName(registration) }}
                  </button>

                  <p v-if="eventCaption(registration)" class="line-clamp-2 text-sm text-muted">
                    {{ eventCaption(registration) }}
                  </p>
                </div>

                <RouterLink
                  v-if="
                    isSlotBoostMode(registration) &&
                    (slotBoostCount(registration) > 0 || canBoostSlot(registration))
                  "
                  :to="`/registrations/${registration.registrationId}`"
                  class="shrink-0"
                  @click.stop
                >
                  <UBadge
                    color="primary"
                    :variant="slotBoostCount(registration) > 0 ? 'solid' : 'outline'"
                    icon="i-lucide-rocket"
                    :label="
                      slotBoostCount(registration) > 0
                        ? t('pages.registrations.boost.slotBoosted')
                        : t('pages.registrations.boost.boostASlot')
                    "
                  />
                </RouterLink>

                <button
                  v-if="shouldShowBoostToggle(registration)"
                  type="button"
                  class="boost-button shrink-0"
                  :class="{
                    'boost-button--active': isBoosted(registration),
                    'boost-button--activated':
                      activatedBoostRegistrationId === registration.registrationId,
                  }"
                  :aria-label="boostButtonLabel(registration)"
                  :title="boostButtonLabel(registration)"
                  :disabled="
                    !canToggleBoost(registration) ||
                    boostToggleLoadingId === registration.registrationId
                  "
                  @click.stop="toggleBoost(registration)"
                >
                  <UIcon
                    name="i-lucide-loader-circle"
                    v-if="boostToggleLoadingId === registration.registrationId"
                    class="animate-spin"
                    aria-hidden="true"
                  />
                  <span v-else class="boost-rocket" aria-hidden="true">🚀</span>
                </button>
              </div>

              <div
                v-if="
                  waitlistPositionLabel(registration) ||
                  (registration.status === RegistrationStatus.Offer &&
                    registration.confirmationDeadline)
                "
                class="mt-3 space-y-1"
              >
                <div v-if="waitlistPositionLabel(registration)" class="text-sm text-muted">
                  {{ waitlistPositionLabel(registration) }}
                </div>
                <div
                  v-if="
                    registration.status === RegistrationStatus.Offer &&
                    registration.confirmationDeadline
                  "
                  class="text-sm text-orange-700 dark:text-orange-400"
                >
                  {{
                    t('pages.registrations.pendingOffer', {
                      deadline: d(new Date(registration.confirmationDeadline), 'datetime'),
                    })
                  }}
                </div>
              </div>

              <dl class="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div v-if="!isQuestionnaireRegistration(registration)" class="min-w-0">
                  <dt class="text-xs font-medium text-muted">
                    {{ t('pages.registrations.columns.group') }}
                  </dt>
                  <dd class="mt-0.5 truncate text-neutral-800 dark:text-neutral-100">
                    {{ groupNames(registration) || '-' }}
                  </dd>
                </div>

                <div v-if="!isQuestionnaireRegistration(registration)" class="min-w-0">
                  <dt class="text-xs font-medium text-muted">
                    {{ t('pages.registrations.columns.usage') }}
                  </dt>
                  <dd class="mt-0.5 text-neutral-800 dark:text-neutral-100">
                    {{ usageText(registration) }}
                  </dd>
                </div>

                <div class="col-span-2 min-w-0">
                  <dt class="text-xs font-medium text-muted">
                    {{
                      isQuestionnaireRegistration(registration)
                        ? t('pages.offer.details.responsePeriod')
                        : t('pages.registrations.columns.registrationPeriod')
                    }}
                  </dt>
                  <dd class="mt-0.5 text-neutral-800 dark:text-neutral-100">
                    <span class="font-medium">{{ registrationPeriodState(registration) }}</span>
                    <span class="text-muted">
                      - {{ registrationSchedule(registration).label }}
                      {{ registrationSchedule(registration).date }}
                    </span>
                  </dd>
                </div>

                <div v-if="!isQuestionnaireRegistration(registration)" class="col-span-2 min-w-0">
                  <dt class="text-xs font-medium text-muted">
                    {{ t('pages.registrations.columns.assignedSlot') }}
                  </dt>
                  <dd class="mt-0.5 text-neutral-800 dark:text-neutral-100">
                    <template v-if="registration.appointmentStart">
                      {{ d(new Date(registration.appointmentStart), 'datetime') }}
                      <template v-if="registration.appointmentEnd">
                        - {{ d(new Date(registration.appointmentEnd), 'timeonly') }}
                      </template>
                    </template>
                    <template v-else-if="getAssignedSlotDetails(registration)?.slot">
                      {{ slotDisplayName(getAssignedSlotDetails(registration)!.slot!) }}
                      <span
                        v-if="slotTimeInfo(getAssignedSlotDetails(registration)!.slot!)"
                        class="text-muted"
                      >
                        - {{ slotTimeInfo(getAssignedSlotDetails(registration)!.slot!) }}
                      </span>
                    </template>
                    <template v-else>
                      {{
                        registration.assignedSlotName || t('pages.registrations.assignedSlot.none')
                      }}
                    </template>
                  </dd>
                </div>

                <div v-if="registration.feeAmount != null" class="col-span-2 min-w-0">
                  <dt class="text-xs font-medium text-muted">
                    {{ t('pages.registrations.columns.fee') }}
                  </dt>
                  <dd
                    class="mt-0.5 flex flex-wrap items-center gap-2 text-neutral-800 dark:text-neutral-100"
                  >
                    <span class="font-semibold">{{ n(registration.feeAmount, 'currency') }}</span>
                    <UBadge
                      v-if="registration.feeState"
                      :color="toAppColor(feeStateSeverity(registration.feeState))"
                    >
                      {{ t(`components.registration.feeCell.${registration.feeState}`) }}
                    </UBadge>
                  </dd>
                </div>
              </dl>

              <AppAlert
                v-if="assignedSlotErrorText(registration)"
                color="warning"
                :closable="false"
                class="mt-3 text-xs"
              >
                {{ assignedSlotErrorText(registration) }}
              </AppAlert>

              <div
                v-if="registration.attachments?.length"
                class="mt-3 rounded-lg border border-neutral-200 p-3 dark:border-neutral-700"
              >
                <div class="mb-2 text-xs font-semibold text-muted uppercase">
                  {{ t('pages.registrations.files.title') }}
                </div>
                <div
                  v-for="attachment in registration.attachments"
                  :key="attachment.id"
                  class="flex items-center gap-2 py-1"
                >
                  <UIcon name="i-lucide-file" class="text-dimmed" aria-hidden="true" />
                  <div class="min-w-0 flex-1">
                    <div class="text-sm font-medium [overflow-wrap:anywhere]">
                      {{ attachment.fileName }}
                      <UIcon
                        name="i-lucide-lock"
                        v-if="attachment.isSensitive"
                        class="ml-1 inline-block text-xs text-orange-700 dark:text-orange-400"
                        :title="t('pages.registrations.files.sensitive')"
                        aria-hidden="true"
                      />
                      <span v-if="attachment.isSensitive" class="sr-only">
                        , {{ t('pages.registrations.files.sensitive') }}
                      </span>
                    </div>
                    <div class="text-xs text-muted">
                      {{ d(new Date(attachment.createdAt), 'dateonly') }} ·
                      {{ formatAttachmentSize(attachment.size) }}
                    </div>
                  </div>
                  <UButton
                    icon="i-lucide-download"
                    :aria-label="
                      t('pages.registrations.files.download', { name: attachment.fileName })
                    "
                    :disabled="attachmentDeleteLoadingId === attachment.id"
                    @click.stop="
                      downloadRegistrationAttachment(
                        registration,
                        attachment.id,
                        attachment.fileName,
                      )
                    "
                    variant="ghost"
                  />
                  <UButton
                    icon="i-lucide-trash"
                    color="error"
                    :loading="attachmentDeleteLoadingId === attachment.id"
                    :aria-label="
                      t('pages.registrations.files.delete', { name: attachment.fileName })
                    "
                    @click.stop="
                      confirmDeleteRegistrationAttachment(
                        registration,
                        attachment.id,
                        attachment.fileName,
                      )
                    "
                    variant="ghost"
                  />
                </div>
              </div>

              <details
                v-if="
                  registration.feeAmount != null &&
                  (showFeeNotice(registration) || registration.feeSepaQrCodeDataUrl)
                "
                class="registration-details mt-3 rounded-md border border-neutral-200 bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-800"
                @click.stop
              >
                <summary
                  class="flex min-h-9 cursor-pointer items-center gap-2 px-3 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-200"
                >
                  <UIcon
                    name="i-lucide-credit-card"
                    class="text-[var(--app-text-primary)]"
                    aria-hidden="true"
                  />
                  {{ t('pages.registrations.fee.paymentNotice') }}
                  <UIcon
                    name="i-lucide-chevron-down"
                    class="registration-details-chevron ml-auto text-xs"
                    aria-hidden="true"
                  />
                </summary>
                <div
                  class="grid gap-3 border-t border-neutral-200 p-3 text-xs sm:grid-cols-[minmax(0,1fr)_auto] dark:border-neutral-700"
                >
                  <div
                    v-if="showFeeNotice(registration)"
                    class="whitespace-pre-line text-neutral-600 dark:text-neutral-300"
                  >
                    {{ feeNotificationText(registration) }}
                  </div>
                  <div v-if="registration.feeSepaQrCodeDataUrl" class="space-y-2">
                    <div class="font-medium text-neutral-700 dark:text-neutral-200">
                      {{ t('pages.registrations.fee.sepaQrCode') }}
                    </div>
                    <img
                      :src="registration.feeSepaQrCodeDataUrl"
                      :alt="t('pages.registrations.fee.sepaQrCodeAlt')"
                      class="h-40 w-40 rounded-md bg-white object-contain"
                    />
                  </div>
                </div>
              </details>

              <div
                class="mt-4 flex flex-wrap justify-end gap-2 border-t border-neutral-100 pt-3 dark:border-neutral-800"
              >
                <UButton
                  v-if="registration.status === RegistrationStatus.Offer"
                  icon="i-lucide-check"
                  :label="t('pages.registrations.actions.acceptOffer')"
                  :size="isPhone ? 'lg' : 'sm'"
                  color="success"
                  :loading="isOfferResponseLoading(registration.registrationId)"
                  :disabled="isOfferResponseLoading(registration.registrationId)"
                  @click.stop="confirmAcceptOffer(registration)"
                  variant="outline"
                />
                <UButton
                  v-if="registration.status === RegistrationStatus.Offer"
                  icon="i-lucide-ban"
                  :label="t('pages.registrations.actions.declineOffer')"
                  :size="isPhone ? 'lg' : 'sm'"
                  color="error"
                  :loading="isOfferResponseLoading(registration.registrationId)"
                  :disabled="isOfferResponseLoading(registration.registrationId)"
                  @click.stop="confirmDeclineOffer(registration)"
                  variant="outline"
                />
                <UButton
                  v-if="!isAppointmentRegistration(registration)"
                  icon="i-lucide-pencil"
                  :label="isPhone ? editLabel(registration) : undefined"
                  :aria-label="editLabel(registration)"
                  :title="editLabel(registration)"
                  :size="isPhone ? 'lg' : 'sm'"
                  color="neutral"
                  :disabled="
                    isOfferResponseLoading(registration.registrationId) ||
                    isEditDisabled(registration)
                  "
                  @click.stop="goToEdit(registration)"
                  variant="outline"
                />
                <UButton
                  v-if="registration.contactEmail"
                  icon="i-lucide-mail"
                  :label="isPhone ? t('pages.registrations.actions.contact') : undefined"
                  :aria-label="t('pages.registrations.actions.contact')"
                  :title="t('pages.registrations.actions.contact')"
                  :size="isPhone ? 'lg' : 'sm'"
                  color="neutral"
                  @click.stop="contactRegistration = registration"
                  variant="outline"
                />
                <UButton
                  icon="i-lucide-x"
                  :label="isPhone ? unregisterLabel(registration) : undefined"
                  :aria-label="unregisterLabel(registration)"
                  :title="unregisterLabel(registration)"
                  :size="isPhone ? 'lg' : 'sm'"
                  color="error"
                  :disabled="
                    isOfferResponseLoading(registration.registrationId) ||
                    !isDeregistrationPossible(registration)
                  "
                  @click.stop="confirmUnregister(registration)"
                  variant="outline"
                />
              </div>
            </article>
          </div>

          <div v-else class="overflow-x-auto">
            <table class="min-w-full border-separate border-spacing-y-1 text-left">
              <thead class="text-sm text-muted">
                <tr>
                  <th class="hidden px-3 py-2 font-semibold 2xl:table-cell">
                    {{ t('pages.registrations.columns.type') }}
                  </th>
                  <th class="px-3 py-2 font-semibold">
                    {{ t('pages.registrations.columns.event') }}
                  </th>
                  <th class="px-3 py-2 font-semibold">
                    {{ t('pages.registrations.columns.status') }}
                  </th>
                  <th class="px-3 py-2 font-semibold">
                    {{ t('pages.registrations.columns.group') }}
                  </th>
                  <th class="px-3 py-2 font-semibold">
                    {{ t('pages.registrations.columns.registrationShort') }}
                  </th>
                  <th class="px-3 py-2 font-semibold">
                    {{ t('pages.registrations.columns.usage') }}
                  </th>
                  <th v-if="showBoostColumn" class="px-3 py-2 font-semibold">
                    {{ t('pages.registrations.columns.boost') }}
                  </th>
                  <th class="px-3 py-2 font-semibold">
                    <UTooltip :text="t('pages.registrations.columns.assignedSlot')">
                      <span class="cursor-help underline decoration-dotted underline-offset-4">
                        {{ t('pages.registrations.columns.assignedSlotShort') }}
                      </span>
                    </UTooltip>
                  </th>
                  <th v-if="showFeeColumn" class="px-3 py-2 font-semibold">
                    {{ t('pages.registrations.columns.fee') }}
                  </th>
                  <th class="sticky right-0 bg-default px-3 py-2 text-right font-semibold">
                    {{ t('common.labels.actions') }}
                  </th>
                </tr>
              </thead>
              <tbody class="text-sm text-neutral-700 dark:text-neutral-200">
                <tr
                  v-for="registration in filteredRegistrations"
                  :key="registration.registrationId"
                  class="cursor-pointer rounded-lg bg-white shadow-sm transition-colors hover:bg-neutral-50 dark:bg-neutral-900 dark:hover:bg-neutral-800"
                  @click="openPreview(registration)"
                >
                  <td class="hidden px-3 py-2.5 align-top 2xl:table-cell">
                    <UBadge color="neutral">
                      {{ t(`enums.eventType.${registration.eventType}`) }}
                    </UBadge>
                  </td>
                  <td class="min-w-52 px-3 py-2.5 align-top">
                    <div class="space-y-1">
                      <button
                        type="button"
                        class="text-left font-semibold text-[var(--app-text-primary)] hover:underline"
                        @click.stop="goToEvent(registration)"
                      >
                        {{ eventName(registration) }}
                      </button>
                      <div class="text-xs text-muted">
                        {{
                          t('pages.registrations.registrationIdLabel', {
                            id: registration.registrationId,
                          })
                        }}
                      </div>
                      <div v-if="eventCaption(registration)" class="max-w-80 text-xs text-muted">
                        {{ eventCaption(registration) }}
                      </div>
                      <UButton
                        v-if="registration.joinCode"
                        class="mt-1"
                        icon="i-lucide-link"
                        color="neutral"
                        variant="subtle"
                        size="xs"
                        :label="t('pages.registrations.studyGroup.shareInvite')"
                        @click.stop="copyStudyGroupInvite(registration)"
                      />
                      <div v-if="registration.attachments?.length" class="mt-2 space-y-1.5">
                        <div class="text-xs font-semibold text-muted uppercase">
                          {{ t('pages.registrations.files.title') }}
                        </div>
                        <div
                          v-for="attachment in registration.attachments"
                          :key="attachment.id"
                          class="flex max-w-96 items-start gap-1"
                        >
                          <button
                            type="button"
                            class="group inline-flex max-w-[22rem] min-w-0 items-start gap-2 text-left text-xs text-[var(--app-text-primary)]"
                            :disabled="attachmentDeleteLoadingId === attachment.id"
                            @click.stop="
                              downloadRegistrationAttachment(
                                registration,
                                attachment.id,
                                attachment.fileName,
                              )
                            "
                          >
                            <UIcon name="i-lucide-download" aria-hidden="true" />
                            <span class="min-w-0">
                              <span
                                class="block font-medium [overflow-wrap:anywhere] group-hover:underline"
                              >
                                {{ attachment.fileName }}
                                <UIcon
                                  name="i-lucide-lock"
                                  v-if="attachment.isSensitive"
                                  class="ml-1 inline-block text-orange-700 dark:text-orange-400"
                                  :title="t('pages.registrations.files.sensitive')"
                                  aria-hidden="true"
                                />
                                <span v-if="attachment.isSensitive" class="sr-only">
                                  , {{ t('pages.registrations.files.sensitive') }}
                                </span>
                              </span>
                              <span class="block text-muted">
                                {{ d(new Date(attachment.createdAt), 'dateonly') }} ·
                                {{ formatAttachmentSize(attachment.size) }}
                              </span>
                            </span>
                          </button>
                          <UButton
                            icon="i-lucide-trash"
                            color="error"
                            size="sm"
                            :loading="attachmentDeleteLoadingId === attachment.id"
                            :aria-label="
                              t('pages.registrations.files.delete', { name: attachment.fileName })
                            "
                            @click.stop="
                              confirmDeleteRegistrationAttachment(
                                registration,
                                attachment.id,
                                attachment.fileName,
                              )
                            "
                            variant="ghost"
                          />
                        </div>
                      </div>
                    </div>
                  </td>

                  <td class="px-3 py-2.5 align-top">
                    <div class="space-y-1">
                      <UBadge :color="registrationStatusColor(registration.status)">
                        {{ registrationStatusLabel(registration) }}
                      </UBadge>
                      <div
                        v-if="waitlistPositionLabel(registration, true)"
                        class="text-xs text-muted"
                        :title="waitlistPositionLabel(registration) ?? undefined"
                      >
                        {{ waitlistPositionLabel(registration, true) }}
                      </div>
                      <div
                        v-if="
                          registration.status === RegistrationStatus.Offer &&
                          registration.confirmationDeadline
                        "
                        class="text-xs text-orange-700 dark:text-orange-400"
                      >
                        {{
                          t('pages.registrations.pendingOffer', {
                            deadline: d(new Date(registration.confirmationDeadline), 'datetime'),
                          })
                        }}
                      </div>
                    </div>
                  </td>
                  <td class="px-3 py-2.5 align-top">
                    <span v-if="groupNames(registration)">{{ groupNames(registration) }}</span>
                    <span v-else class="text-dimmed">-</span>
                  </td>
                  <td class="px-3 py-2.5 align-top">
                    <div class="space-y-1">
                      <UBadge :color="registrationPeriodInfo(registration).color" size="sm">
                        {{ registrationPeriodState(registration) }}
                      </UBadge>
                      <div class="text-xs text-muted">
                        {{ registrationSchedule(registration).label }}
                        <span class="whitespace-nowrap">
                          {{ registrationSchedule(registration).date }}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td class="px-3 py-2.5 align-top">
                    <span v-if="isQuestionnaireRegistration(registration)" class="text-dimmed"
                      >-</span
                    >
                    <template v-else>{{ usageText(registration) }}</template>
                  </td>
                  <td v-if="showBoostColumn" class="px-3 py-2.5 align-top">
                    <div class="flex flex-col items-start gap-1">
                      <RouterLink
                        v-if="
                          isSlotBoostMode(registration) &&
                          (slotBoostCount(registration) > 0 || canBoostSlot(registration))
                        "
                        :to="`/registrations/${registration.registrationId}`"
                        @click.stop
                      >
                        <UBadge
                          color="primary"
                          :variant="slotBoostCount(registration) > 0 ? 'solid' : 'outline'"
                          icon="i-lucide-rocket"
                          :label="
                            slotBoostCount(registration) > 0
                              ? t('pages.registrations.boost.slotBoosted')
                              : t('pages.registrations.boost.boostASlot')
                          "
                        />
                      </RouterLink>
                      <button
                        v-if="shouldShowBoostToggle(registration)"
                        type="button"
                        class="boost-button"
                        :class="{
                          'boost-button--active': isBoosted(registration),
                          'boost-button--activated':
                            activatedBoostRegistrationId === registration.registrationId,
                        }"
                        :aria-label="boostButtonLabel(registration)"
                        :title="boostButtonLabel(registration)"
                        :disabled="
                          !canToggleBoost(registration) ||
                          boostToggleLoadingId === registration.registrationId
                        "
                        @click.stop="toggleBoost(registration)"
                      >
                        <UIcon
                          name="i-lucide-loader-circle"
                          v-if="boostToggleLoadingId === registration.registrationId"
                          class="animate-spin"
                          aria-hidden="true"
                        />
                        <span v-else class="boost-rocket" aria-hidden="true">🚀</span>
                      </button>
                      <small v-if="shouldShowBoostToggle(registration)" class="max-w-48 text-muted">
                        {{ boostStateMessage(registration) }}
                      </small>
                    </div>
                  </td>
                  <td class="px-3 py-2.5 align-top">
                    <span v-if="isQuestionnaireRegistration(registration)" class="text-dimmed"
                      >-</span
                    >
                    <div v-else class="space-y-2">
                      <div
                        v-if="registration.appointmentStart"
                        class="rounded-md border border-neutral-200 bg-neutral-50 p-2 text-sm dark:border-neutral-700 dark:bg-neutral-800"
                      >
                        <div class="flex items-start gap-2">
                          <UIcon
                            name="i-lucide-calendar-clock"
                            class="mt-0.5 text-[var(--app-text-primary)]"
                          />
                          <div class="min-w-0 space-y-1">
                            <div class="font-medium">
                              {{ d(new Date(registration.appointmentStart), 'datetime') }}
                              <template v-if="registration.appointmentEnd">
                                - {{ d(new Date(registration.appointmentEnd), 'timeonly') }}
                              </template>
                            </div>
                            <div class="text-muted">
                              {{
                                locale === 'de'
                                  ? registration.appointmentBookingTypeNameDE
                                  : registration.appointmentBookingTypeNameEN ||
                                    registration.appointmentBookingTypeNameDE
                              }}
                            </div>
                            <div
                              v-if="
                                getAssignedSlotDetails(registration)?.slot?.assignedUserDisplayName
                              "
                              class="text-muted"
                            >
                              {{
                                getAssignedSlotDetails(registration)!.slot!.assignedUserDisplayName
                              }}
                            </div>
                            <div
                              v-if="getAssignedSlotDetails(registration)?.slot?.location"
                              class="text-muted"
                            >
                              {{ getAssignedSlotDetails(registration)!.slot!.location }}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        v-else-if="getAssignedSlotDetails(registration)?.slot"
                        class="text-sm text-neutral-700 dark:text-neutral-200"
                      >
                        <div class="font-medium">
                          {{ slotDisplayName(getAssignedSlotDetails(registration)!.slot!) }}
                        </div>
                        <div
                          v-if="slotTimeInfo(getAssignedSlotDetails(registration)!.slot!)"
                          class="text-muted"
                        >
                          {{ slotTimeInfo(getAssignedSlotDetails(registration)!.slot!) }}
                        </div>
                        <div
                          v-if="getAssignedSlotDetails(registration)!.slot!.assignedUserDisplayName"
                          class="text-muted"
                        >
                          {{ getAssignedSlotDetails(registration)!.slot!.assignedUserDisplayName }}
                        </div>
                        <div
                          v-if="getAssignedSlotDetails(registration)!.slot!.location"
                          class="text-muted"
                        >
                          {{ getAssignedSlotDetails(registration)!.slot!.location }}
                        </div>
                        <div
                          v-if="getAssignedSlotDetails(registration)!.slot!.description"
                          class="text-muted"
                          v-html="getAssignedSlotDetails(registration)!.slot!.description"
                        />
                        <div
                          v-if="publicCampusEventUrl(getAssignedSlotDetails(registration)!.slot!)"
                        >
                          <a
                            :href="
                              publicCampusEventUrl(getAssignedSlotDetails(registration)!.slot!)!
                            "
                            target="_blank"
                            rel="noopener noreferrer"
                            class="inline-flex items-center gap-1 text-[var(--app-text-primary)] hover:underline"
                            @click.stop
                          >
                            <UIcon name="i-lucide-external-link" class="text-xs" />
                            {{ t('pages.offer.details.openInCampus') }}
                          </a>
                        </div>
                      </div>

                      <span v-else>
                        {{
                          registration.assignedSlotName ||
                          t('pages.registrations.assignedSlot.none')
                        }}
                      </span>

                      <AppAlert
                        v-if="assignedSlotErrorText(registration)"
                        color="warning"
                        :closable="false"
                        class="text-xs"
                      >
                        {{ assignedSlotErrorText(registration) }}
                      </AppAlert>
                    </div>
                  </td>
                  <td v-if="showFeeColumn" class="min-w-48 px-3 py-2.5 align-top">
                    <div v-if="registration.feeAmount != null" class="space-y-2">
                      <div class="flex flex-wrap items-center gap-2">
                        <span class="font-semibold">{{
                          n(registration.feeAmount, 'currency')
                        }}</span>
                        <UBadge
                          v-if="registration.feeState"
                          :color="toAppColor(feeStateSeverity(registration.feeState))"
                        >
                          {{ t(`components.registration.feeCell.${registration.feeState}`) }}
                        </UBadge>
                      </div>

                      <div v-if="registration.feeReferenceId" class="text-xs text-muted">
                        {{
                          t('pages.registrations.fee.reference', {
                            reference: registration.feeReferenceId,
                          })
                        }}
                      </div>

                      <details
                        v-if="showFeeNotice(registration) || registration.feeSepaQrCodeDataUrl"
                        class="registration-details rounded-md border border-neutral-200 dark:border-neutral-700"
                        @click.stop
                      >
                        <summary
                          class="flex min-h-9 cursor-pointer items-center gap-2 px-2 py-1.5 text-xs font-medium text-neutral-700 dark:text-neutral-200"
                        >
                          <UIcon
                            name="i-lucide-credit-card"
                            class="text-[var(--app-text-primary)]"
                            aria-hidden="true"
                          />
                          {{ t('pages.registrations.fee.paymentNotice') }}
                          <UIcon
                            name="i-lucide-chevron-down"
                            class="registration-details-chevron ml-auto text-xs"
                            aria-hidden="true"
                          />
                        </summary>
                        <div
                          class="space-y-3 border-t border-neutral-200 p-2 dark:border-neutral-700"
                        >
                          <div
                            v-if="showFeeNotice(registration)"
                            class="text-xs whitespace-pre-line text-neutral-600 dark:text-neutral-300"
                          >
                            {{ feeNotificationText(registration) }}
                          </div>
                          <div v-if="registration.feeSepaQrCodeDataUrl" class="space-y-2">
                            <div class="text-xs font-medium text-neutral-700 dark:text-neutral-200">
                              {{ t('pages.registrations.fee.sepaQrCode') }}
                            </div>
                            <img
                              :src="registration.feeSepaQrCodeDataUrl"
                              :alt="t('pages.registrations.fee.sepaQrCodeAlt')"
                              class="h-40 w-40 rounded-md bg-white object-contain"
                            />
                          </div>
                        </div>
                      </details>
                    </div>
                    <span v-else class="text-dimmed">
                      {{ t('pages.registrations.fee.none') }}
                    </span>
                  </td>
                  <td class="sticky right-0 bg-inherit px-3 py-2.5 text-right align-top">
                    <div class="flex flex-wrap justify-end gap-1">
                      <UButton
                        v-if="registration.status === RegistrationStatus.Offer"
                        icon="i-lucide-check"
                        :label="t('pages.registrations.actions.acceptOffer')"
                        size="sm"
                        color="success"
                        :loading="isOfferResponseLoading(registration.registrationId)"
                        :disabled="isOfferResponseLoading(registration.registrationId)"
                        @click.stop="confirmAcceptOffer(registration)"
                        variant="outline"
                      />
                      <UButton
                        v-if="registration.status === RegistrationStatus.Offer"
                        icon="i-lucide-ban"
                        :label="t('pages.registrations.actions.declineOffer')"
                        size="sm"
                        color="error"
                        :loading="isOfferResponseLoading(registration.registrationId)"
                        :disabled="isOfferResponseLoading(registration.registrationId)"
                        @click.stop="confirmDeclineOffer(registration)"
                        variant="outline"
                      />
                      <UButton
                        v-if="!isAppointmentRegistration(registration)"
                        icon="i-lucide-pencil"
                        :aria-label="editLabel(registration)"
                        :title="editLabel(registration)"
                        size="sm"
                        color="neutral"
                        :disabled="
                          isOfferResponseLoading(registration.registrationId) ||
                          isEditDisabled(registration)
                        "
                        @click.stop="goToEdit(registration)"
                        variant="outline"
                      />
                      <UButton
                        v-if="registration.contactEmail"
                        icon="i-lucide-mail"
                        :aria-label="t('pages.registrations.actions.contact')"
                        :title="t('pages.registrations.actions.contact')"
                        size="sm"
                        color="neutral"
                        @click.stop="contactRegistration = registration"
                        variant="outline"
                      />
                      <UButton
                        icon="i-lucide-x"
                        :aria-label="unregisterLabel(registration)"
                        :title="unregisterLabel(registration)"
                        size="sm"
                        color="error"
                        :disabled="
                          isOfferResponseLoading(registration.registrationId) ||
                          !isDeregistrationPossible(registration)
                        "
                        @click.stop="confirmUnregister(registration)"
                        variant="outline"
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </UCard>

    <PersonalCalendarFeedCard class="mt-6" />

    <AppSlideover
      :visible="previewVisible"
      position="right"
      class="w-full md:w-[34rem]"
      :header="t('pages.registrations.preview.title')"
      @update:visible="handlePreviewVisibility"
    >
      <div v-if="previewRegistration" class="space-y-5">
        <div>
          <div class="text-xl font-semibold text-neutral-900 dark:text-white">
            {{ eventName(previewRegistration) }}
          </div>
          <div v-if="eventCaption(previewRegistration)" class="mt-1 text-sm text-muted">
            {{ eventCaption(previewRegistration) }}
          </div>
          <div class="mt-3 flex flex-wrap gap-2">
            <UBadge color="neutral">
              {{ t(`enums.eventType.${previewRegistration.eventType}`) }}
            </UBadge>
            <UBadge :color="registrationStatusColor(previewRegistration.status)">
              {{ registrationStatusLabel(previewRegistration) }}
            </UBadge>
          </div>
        </div>

        <div class="grid gap-3 text-sm">
          <div class="rounded-md border border-neutral-200 p-3 dark:border-neutral-700">
            <div class="text-xs font-medium text-muted uppercase">
              {{ t('pages.registrations.columns.group') }}
            </div>
            <div class="mt-1">{{ groupNames(previewRegistration) || '-' }}</div>
          </div>

          <div class="rounded-md border border-neutral-200 p-3 dark:border-neutral-700">
            <div class="text-xs font-medium text-muted uppercase">
              {{ t('pages.registrations.columns.registrationPeriod') }}
            </div>
            <div class="mt-1 font-medium">{{ registrationPeriodState(previewRegistration) }}</div>
            <div class="text-muted">{{ registrationPeriodText(previewRegistration) }}</div>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div class="rounded-md border border-neutral-200 p-3 dark:border-neutral-700">
              <div class="text-xs font-medium text-muted uppercase">
                {{ t('pages.registrations.columns.usage') }}
              </div>
              <div class="mt-1">{{ usageText(previewRegistration) }}</div>
            </div>
            <div class="rounded-md border border-neutral-200 p-3 dark:border-neutral-700">
              <div class="text-xs font-medium text-muted uppercase">
                {{ t('pages.registrations.columns.firstDrawDate') }}
              </div>
              <div class="mt-1">
                <template v-if="previewRegistration.firstDrawDate">
                  {{ d(new Date(previewRegistration.firstDrawDate), 'datetime') }}
                </template>
                <template v-else>-</template>
              </div>
            </div>
          </div>

          <div class="rounded-md border border-neutral-200 p-3 dark:border-neutral-700">
            <div class="text-xs font-medium text-muted uppercase">
              {{ t('pages.registrations.columns.assignedSlot') }}
            </div>
            <div class="mt-1">
              <template v-if="getAssignedSlotDetails(previewRegistration)?.slot">
                <div class="font-medium">
                  {{ slotDisplayName(getAssignedSlotDetails(previewRegistration)!.slot!) }}
                </div>
                <div
                  v-if="slotTimeInfo(getAssignedSlotDetails(previewRegistration)!.slot!)"
                  class="text-muted"
                >
                  {{ slotTimeInfo(getAssignedSlotDetails(previewRegistration)!.slot!) }}
                </div>
              </template>
              <template v-else>
                {{
                  previewRegistration.assignedSlotName || t('pages.registrations.assignedSlot.none')
                }}
              </template>
            </div>
          </div>

          <div
            v-if="previewRegistration.feeAmount != null"
            class="rounded-md border border-neutral-200 p-3 dark:border-neutral-700"
          >
            <div class="text-xs font-medium text-muted uppercase">
              {{ t('pages.registrations.columns.fee') }}
            </div>
            <div class="mt-1 flex flex-wrap items-center gap-2">
              <span class="font-semibold">{{ n(previewRegistration.feeAmount, 'currency') }}</span>
              <UBadge
                v-if="previewRegistration.feeState"
                :color="toAppColor(feeStateSeverity(previewRegistration.feeState))"
              >
                {{ t(`components.registration.feeCell.${previewRegistration.feeState}`) }}
              </UBadge>
            </div>
          </div>
        </div>

        <div class="flex flex-wrap gap-2">
          <UButton
            icon="i-lucide-external-link"
            :label="t('pages.registrations.preview.openEvent')"
            size="sm"
            @click="goToEvent(previewRegistration)"
            variant="outline"
          />
          <UButton
            v-if="!isAppointmentRegistration(previewRegistration)"
            icon="i-lucide-pencil"
            :label="editLabel(previewRegistration)"
            size="sm"
            color="neutral"
            :disabled="isEditDisabled(previewRegistration)"
            @click="goToEdit(previewRegistration)"
            variant="outline"
          />
          <UButton
            icon="i-lucide-x"
            :label="unregisterLabel(previewRegistration)"
            size="sm"
            color="error"
            :disabled="
              isOfferResponseLoading(previewRegistration.registrationId) ||
              !isDeregistrationPossible(previewRegistration)
            "
            @click="unregisterFromPreview(previewRegistration)"
            variant="outline"
          />
        </div>
      </div>
    </AppSlideover>

    <AppModal
      :open="!!contactRegistration"
      modal
      :title="t('pages.registrations.contact.title')"
      class="w-full max-w-lg"
      @update:open="handleContactDialogVisibility"
    >
      <div v-if="contactRegistration" class="space-y-4">
        <div>
          <div class="font-semibold text-neutral-900 dark:text-white">
            {{ eventName(contactRegistration) }}
          </div>
          <div v-if="eventCaption(contactRegistration)" class="text-sm text-muted">
            {{ eventCaption(contactRegistration) }}
          </div>
        </div>

        <div class="rounded-md border border-neutral-200 p-3 dark:border-neutral-700">
          <div class="text-sm font-medium text-neutral-700 dark:text-neutral-200">
            {{ t('pages.registrations.contact.organizer') }}
          </div>
          <a
            v-if="contactRegistration.contactEmail"
            :href="contactMailto(contactRegistration)"
            class="mt-1 inline-flex items-center gap-2 text-[var(--app-text-primary)] hover:underline"
          >
            <UIcon name="i-lucide-mail" class="text-sm" />
            {{ contactRegistration.contactEmail }}
          </a>
          <div v-if="contactRegistration.lecturerDisplayName" class="mt-2 text-sm text-muted">
            {{ contactRegistration.lecturerDisplayName }}
          </div>
        </div>
      </div>

      <template #footer>
        <UButton
          :label="t('common.actions.close')"
          color="neutral"
          @click="contactRegistration = null"
          variant="ghost"
        />
      </template>
    </AppModal>
  </div>
</template>

<style scoped>
.registration-details > summary {
  list-style: none;
}

.registration-details > summary::-webkit-details-marker {
  display: none;
}

.registration-details-chevron {
  transition: transform 160ms ease;
}

.registration-details[open] .registration-details-chevron {
  transform: rotate(180deg);
}

.registration-details > summary:focus-visible {
  border-radius: inherit;
  outline: 2px solid var(--ui-primary);
  outline-offset: 2px;
}

.boost-button {
  cursor: pointer;
  position: relative;
  display: inline-flex;
  width: 2.25rem;
  height: 2.25rem;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: inherit;
  transition:
    background-color 160ms ease,
    transform 160ms ease;
}

.boost-button:hover:not(:disabled) {
  background: color-mix(in srgb, var(--ui-primary) 12%, transparent);
}

.boost-button:focus-visible {
  outline: 2px solid var(--ui-primary);
  outline-offset: 2px;
}

.boost-button:disabled {
  opacity: 0.5;
}

.boost-button:not(.boost-button--active) .boost-rocket {
  filter: grayscale(100%);
}

.boost-rocket {
  display: block;
  font-size: 1.35rem;
  line-height: 1;
  transition: filter 160ms ease;
}

.boost-button--activated .boost-rocket {
  animation: boost-pulse 800ms forwards ease-in-out;
  border-radius: 999px;
  box-shadow: 0 5px 28px -11px color-mix(in srgb, var(--smu-boost-flame) 90%, transparent);
}

@keyframes boost-pulse {
  0% {
    box-shadow: 0 0 0 0 color-mix(in srgb, var(--smu-boost-flame) 40%, transparent);
  }

  60% {
    transform: scale(1.5);
  }

  100% {
    box-shadow: 0 0 0 10px color-mix(in srgb, var(--smu-boost-flame) 0%, transparent);
  }
}
</style>
